import moment from "moment-timezone";

export type EventTimezoneOption = {
  name: string;
  value: string;
};

const commonEventTimezones = [
  "Africa/Lagos",
  "America/Halifax",
  "America/Toronto",
  "America/Vancouver",
  "America/New_York",
  "Europe/London",
  "UTC",
  "Africa/Accra",
  "Africa/Abidjan",
  "Africa/Nairobi",
  "Africa/Johannesburg",
  "Africa/Cairo",
  "Africa/Casablanca",
  "America/Los_Angeles",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Mexico_City",
  "America/Bogota",
  "America/Lima",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "America/Jamaica",
  "America/Port_of_Spain",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Zurich",
  "Europe/Stockholm",
  "Europe/Istanbul",
  "Asia/Dubai",
  "Asia/Qatar",
  "Asia/Riyadh",
  "Asia/Jerusalem",
  "Asia/Kolkata",
  "Asia/Karachi",
  "Asia/Dhaka",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Jakarta",
  "Asia/Manila",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Perth",
  "Pacific/Auckland",
];

const timezoneLabelCache = new Map<string, string>();

const getCityName = (timezone: string) => {
  const parts = timezone.split("/");
  return (parts[parts.length - 1] || timezone).replace(/_/g, " ");
};

const getRegionName = (timezone: string) => {
  const parts = timezone.split("/");
  return parts.length > 1 ? parts.slice(0, -1).join(" / ") : "";
};

export const formatTimezoneLabel = (timezone?: string) => {
  if (!timezone) return "";

  const cachedLabel = timezoneLabelCache.get(timezone);
  if (cachedLabel) return cachedLabel;

  const zone = moment.tz.zone(timezone);

  if (!zone) return timezone;

  const zonedNow = moment.tz(timezone);
  const cityName = getCityName(timezone);
  const regionName = getRegionName(timezone);
  const offset = zonedNow.format("Z");
  const abbreviation = zonedNow.zoneAbbr();
  const regionSuffix = regionName ? `, ${regionName}` : "";
  const abbreviationSuffix =
    abbreviation && abbreviation !== offset ? `, ${abbreviation}` : "";

  const label = `${cityName}${regionSuffix} (UTC${offset}${abbreviationSuffix})`;
  timezoneLabelCache.set(timezone, label);
  return label;
};

export const buildEventTimezoneOptions = (
  currentTimezone?: string,
): EventTimezoneOption[] => {
  const timezones = [currentTimezone, ...commonEventTimezones].filter(
    Boolean,
  ) as string[];

  const seen = new Set<string>();
  return timezones.reduce<EventTimezoneOption[]>((options, timezone) => {
    if (!moment.tz.zone(timezone) || seen.has(timezone)) return options;
    seen.add(timezone);

    options.push({
      name: formatTimezoneLabel(timezone),
      value: timezone,
    });

    return options;
  }, []);
};
